// Loaders for the stock screens. One shape for all of them: a useEffect loader
// registered against EVERY table the stock views read (TCSD-0248), so a write
// anywhere re-reads every card showing stock. ⛔ `loading` flips on the first
// load only — a refetch must never unmount a card behind an open window
// (TCSD-0039).

import { useCallback, useEffect, useState } from 'react';
import { useReader } from '../../../shared/save-path';
import { errorMessage } from '../../../shared/errors';
import { READS } from './api';

export function useStockLoad<T>(load: () => Promise<T>, deps: unknown[], initial: T) {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);
  useReader(READS, refetch);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    load()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[dialysis-stock] load failed', e);
        setError(errorMessage((e as { cause?: unknown }).cause ?? e, (e as Error).message || 'Could not load stock.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, refetch };
}
